import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/environment';
import moment from 'moment-timezone';
import { Observable } from 'rxjs';
import { map, mapTo } from 'rxjs/operators';

import {
  convertToProgram,
  convertToProgramPreview,
  ProgramApi,
  ProgramPreviewApi,
} from '../models/programs-api.model';
import { Program, ProgramPlaylist, ProgramPreview } from '../models/programs.model';

const programsUrl = {
  getPrograms: (id: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/programs`,
  postProgram: (id: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program`,
  putProgram: (id: number, programId: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program/edit_program/${programId}`,
  getProgramPlaylists: (id: number, programId: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program/${programId}`,
  postProgramPlaylist: (id: number, programId: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program/add_playlist/${programId}`,
  putProgramPlaylist: (id: number, programId: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program/edit_playlist/${programId}`,
  deleteProgram: (id: number, programId: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program/${programId}`,
  duplicateProgram: (id: number, programId: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program/duplicate/${programId}`,
  deleteProgramPlaylist: (id: number, playlistId: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/del_playlist/${playlistId}`,
  previewProgram: (id: number, programId: number, cycleIdx: number) =>
    `${environment.urls.MAIN_API_V2}/radio/${id}/planification/program/${programId}/preview/${cycleIdx}`,
};
@Injectable({
  providedIn: 'root',
})
export class ProgramsService {
  constructor(private readonly httpClient: HttpClient) {}

  getPrograms(radioId: number): Observable<Program[]> {
    return this.httpClient
      .get<ProgramApi[]>(programsUrl.getPrograms(radioId))
      .pipe(
        map(p => p.map(convertToProgram).sort((a, b) => a.name.localeCompare(b.name))),
      );
  }

  createProgram(radioId: number, program: Partial<Program>): Observable<Program> {
    const payload = {
      program_name: program.name,
      program_color: program.color,
      idtrack_intro: program.idTrackIntro,
      idtrack_extro: program.idTrackExtro,
    };

    return this.httpClient.post<number>(programsUrl.postProgram(radioId), payload).pipe(
      map(id => {
        const newProgram: Program = {
          id,
          name: program.name,
          color: program.color,
          idTrackIntro: program.idTrackIntro,
          idTrackExtro: program.idTrackExtro,
          playlistsNumber: 0,
          playlists: [],
          estimatedDuration: 0,
          createdAt: moment(),
        };

        return newProgram;
      }),
    );
  }

  editProgram(radioId: number, program: Program) {
    const payload = {
      program_name: program.name,
      program_color: program.color,
      idtrack_intro: program.idTrackIntro,
      idtrack_extro: program.idTrackExtro,
    };

    return this.httpClient.put(programsUrl.putProgram(radioId, program.id), payload);
  }

  getProgramPlaylists(radioId: number, programId: number): Observable<Program> {
    return this.httpClient
      .get<ProgramApi>(programsUrl.getProgramPlaylists(radioId, programId))
      .pipe(map(convertToProgram));
  }

  addPlaylistToProgram(
    radioId: number,
    programId: number,
    playlist: Partial<ProgramPlaylist>,
  ): Observable<number> {
    const payload = {
      idplaylist: playlist.idPlaylist,
      tracks_number: playlist.tracksNumber,
      position: playlist.position,
      sorting: playlist.sorting,
    };

    return this.httpClient
      .post(programsUrl.postProgramPlaylist(radioId, programId), payload)
      .pipe(mapTo(programId));
  }

  editProgramPlaylist(
    radioId: number,
    programId: number,
    playlist: Partial<ProgramPlaylist>,
  ) {
    const payload = {
      idprogramplaylist: playlist.idProgramPlaylist,
      idplaylist: playlist.idPlaylist,
      tracks_number: playlist.tracksNumber,
      position: playlist.position,
      sorting: playlist.sorting,
    };

    return this.httpClient
      .put(programsUrl.putProgramPlaylist(radioId, programId), payload)
      .pipe(mapTo(programId));
  }

  deleteProgram(radioId: number, programId: number) {
    return this.httpClient.delete(programsUrl.deleteProgram(radioId, programId));
  }

  duplicateProgram(radioId: number, program: Program): Observable<number> {
    const payload = {
      program_name: program.name,
    };

    return this.httpClient.post<number>(
      programsUrl.duplicateProgram(radioId, program.id),
      payload,
    );
  }

  deleteProgramPlaylist(radioId: number, playlistId: number): Observable<any> {
    return this.httpClient.delete<number>(
      programsUrl.deleteProgramPlaylist(radioId, playlistId),
    );
  }

  previewProgram(
    radioId: number,
    programId: number,
    cycleIdx: number,
  ): Observable<ProgramPreview[]> {
    return this.httpClient
      .get<ProgramPreviewApi[]>(programsUrl.previewProgram(radioId, programId, cycleIdx))
      .pipe(map(p => p.map(convertToProgramPreview)));
  }
}
